import React, { useState } from "react";

import { Link, Notification } from "@stellar/design-system";
import "./styles.scss";

interface NotificationWithButtonsProps {
  variant: "primary" | "secondary" | "success" | "error" | "warning";
  title: string;
  icon?: React.ReactNode;
  buttons?: {
    label: string;
    onClick: () => void;
  }[];
  children: string | React.ReactNode;
  isSimpleNotif?: boolean;
  onClose?: () => void;
}

export const NotificationWithButtons = React.forwardRef<
  HTMLDivElement,
  NotificationWithButtonsProps
>(({ variant, title, icon, buttons, children, isSimpleNotif = false, onClose }, ref) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const showCloseButton = isSimpleNotif || Boolean(onClose);

  if (showCloseButton && isDismissed) return null;

  const wrapperStyle = isSimpleNotif
    ? `NotificationWithButtons__wrapper__simpleNotif`
    : `NotificationWithButtons__wrapper`;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsDismissed(true);
    }
  };

  return (
    <div ref={ref} className={wrapperStyle}>
      <Notification variant={variant} title={title} icon={icon} isFilled={true}>
        <div>{children}</div>

        {buttons && (
          <div className="Notification__buttons">
            {buttons.map((b) => (
              <Link role="button" onClick={b.onClick} key={`btn-${b.label}`}>
                {b.label}
              </Link>
            ))}
          </div>
        )}

        {showCloseButton && (
          <button
            type="button"
            className="NotificationWithButtons__close"
            aria-label="Dismiss notification"
            onClick={handleClose}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </Notification>
    </div>
  );
});

NotificationWithButtons.displayName = "NotificationWithButtons";
